import React, { useState, useEffect, useCallback, useRef } from 'react';

import { Flex, useTheme, MenuItem, Menu, MenuButton, MenuList } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { useQueryClient } from '@tanstack/react-query';
import { staticRoutesWithCustomSidebar } from 'Deer/constants/staticRoutesWithCustomSidebar';
import { useSelectReviewsSelectedIntent } from 'Deer/intent/Reviews/slice/ReviewsIntentSlice';
// Download CSV
import { downloadCSVApi, manageTableCSVReportApi } from 'api/analytics/review';
import { useToast } from 'components/hooks/useToast';
import { useDispatch } from 'react-redux';
import { REVIEW_CYCLE_STEPS_V1 } from 'reducers/reviewCreationSlice';
import {
  setEmpId,
  setIsCalibrationSidepanelOpen,
  useReviewManageSidepanelData,
} from 'reducers/reviewManageSidepanelSlice';
import { useSelectedView } from 'reducers/viewsSlice';
import { checkNewNavEnabled } from 'utils/helpers/featureFlags';

import { Plus } from 'assets/assetsV1/iconComponents';
import { DownloadIcon } from 'assets/assetsV1/iconComponents/DownloadIcon';
import addReviewV1 from 'assets/assetsV1/illustrations/newIllustrations/addReviewV1.svg';

import {
  CSV_DOWNLOAD_API_POLL_INTERVAL,
  CSV_DOWNLOAD_FILE_REQUEST_WAIT_INTERVAL,
  TOAST_STATUSES,
} from 'constants/ProjectConstants';
import { isMobileView } from 'constants/isMobileView';
import { localStorageKeys } from 'constants/localStorageKeys';
import { sendReviewCalibrationOpenEvent } from 'constants/mixpanelHelpers/reviewsEvents';
import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';
import { routes } from 'constants/routes';

import { getReviewsManageFilterData, getStatusOfRequestIds } from 'services/analytics/review';

import { AgGridSearchBar } from 'uiToolkitV1/AgGridSearchBar';
import { AgGridRefreshButton } from 'uiToolkitV1/AgGridWrapperV2/components/RefreshButton/AgGridRefreshButton';
import { Button } from 'uiToolkitV1/Button';
import { EmptyScreen } from 'uiToolkitV1/EmptyScreen';
import { SkeletonLoader } from 'uiToolkitV1/SkeletonLoader';
import { TextBodyMedium, TextLabel } from 'uiToolkitV1/Typography';

import ReviewManageAGGridV1 from '../ReviewManageAGGrid/ReviewManageAGGridV1';
import { reviewStatusValueGetterCSV } from '../ReviewManageAGGrid/components/ReviewStatusV1';
import { employeesValueGetterCSV } from '../ReviewManageAGGrid/components/cellComponents/EmpGroupAvatars';
import { REVIEW_MANAGE_GRID_COLUMN_ID } from '../ReviewManageAGGrid/reviewMangeGridConstants';
import { BulkActionContextProvider } from './components/BulkActions/BulkActionContext';
import { BulkActionHeader } from './components/BulkActions/BulkActionHeader';
import { BulkNotifyContextProvider } from './components/BulkNotifyDrawer/BulkNotifyContext';
import BulkNotifyDrawer from './components/BulkNotifyDrawer/BulkNotifyDrawer';
import { PartialPublishContextProvider } from './components/ShareResultsDrawer/PartialPublishContext';
import ShareResultsDrawer from './components/ShareResultsDrawer/ShareResultsDrawer';

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  padding: '4px',
  borderRadius: '4px',
  ':hover, :focus': {
    backgroundColor: theme.colors.surface.E6F1FD,

    '& p': {
      color: `${theme.colors.button.blue.focus} !important`,
    },
  },
}));

// const StyledSidePanelButton = styled(SidePanelButton)`
//   flex: auto 0 0;
//   text-wrap: wrap;
//   border-radius: 8px;
//   white-space: break-spaces;
// `;

// const sidePanelTabsStructure = {
//   overview: {
//     id: '2',
//     title: 'Overview',
//     content: null,
//     icon: ReviewsOverview,
//   },
//   managerScore: {
//     id: '6',
//     title: 'Manager Review',
//     content: null,
//     icon: ManagerScore,
//   },
//   selfScore: {
//     id: '4',
//     title: 'Self Review',
//     content: null,
//     icon: SelfScore1,
//   },
//   dottedLineManagerScore: {
//     // todo: vikalp dlm what will be id
//     id: '8',
//     title: 'Dotted Line Manager Review',
//     content: null,
//     icon: ReceiverManager1,
//   },
//   peerScore: {
//     id: '5',
//     title: 'Peer Review',
//     content: null,
//     icon: Peers,
//   },
//   upwardScore: {
//     id: '7',
//     title: 'Upward Review',
//     content: null,
//     icon: Leaders,
//   },
//   history: {
//     id: '1',
//     title: 'Calibration Notes',
//     content: null,
//     icon: CalibrationNotes,
//   },

//   pastReviewsChart: {
//     // todo: vikalp calibration check these values
//     id: '99',
//     title: 'Reviews History',
//     content: null,
//     icon: Activities,
//   },
// };

function FileSelectionOptions({ downloadCSVApiData, currentTableCsvExport }) {
  const [isFetching, setIsFetching] = useState(false);
  const toast = useToast();
  const localStorageKey = localStorageKeys.reviewTablesManageCSVDownloads;
  const theme = useTheme();

  const wait = (ms = 1000) =>
    new Promise((resolve) => {
      setTimeout(resolve, ms);
    });

  const downloadManageCSVApi = () => manageTableCSVReportApi({ type: 'reviewee', requestBody: downloadCSVApiData() });

  const downloadToSystem = async (blobData) => {
    try {
      if (blobData) {
        const { fetchedBlob: blob, requestId, name } = blobData;
        // 2. Create blob link to download
        const url = window.URL.createObjectURL(new Blob([blob], { type: 'text/csv' }));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', name);
        // 3. Append to html page
        document.body.appendChild(link);
        // 4. Force download
        link.click();
        // 5. Clean up and remove the link
        link.parentNode.removeChild(link);
        onDownloadToSystemComplete();
      }
    } catch (e) {
      onError(e);
    }
  };

  const showDownloadSuccessToast = () => {
    toast({
      title: 'Download complete!',
      status: 'success',
    });
    // remove from localstorage after toast duration i.e. 5 sec, because user will not have a way to download it anyway
    setTimeout(() => {
      setIsFetching(false);
      window.localStorage.removeItem(localStorageKey);
    }, 5000);
  };

  const showDownloadStartInfoToast = () => {
    toast({
      title: 'Generating report',
      status: 'info',
      duration: 5000,
    });
  };

  const showDownloadAlreadyInProgressToast = () => {
    toast({
      title:
        'Report generation is already in progress! Please wait for that to complete or refresh the page to start again',
      status: 'info',
    });
  };

  const setGeneratedFilesToLS = (res) => {
    console.log('res: ', res);
    try {
      const prevGeneratedFiles = JSON.parse(window.localStorage.getItem(localStorageKey)) ?? [];

      const generatedFiles = [...prevGeneratedFiles, { ...res, requestedTS: Date.now() }];

      const stringifiedGeneratedFiles = JSON.stringify(generatedFiles);

      window.localStorage.setItem(localStorageKey, stringifiedGeneratedFiles);
    } catch (e) {
      console.error('Error in LS : ', e);
    }
  };

  const generateFile = async () => {
    if (isFetching) {
      showDownloadAlreadyInProgressToast();
      return;
    }

    showDownloadStartInfoToast();
    try {
      setIsFetching(true);

      // makes the initial download request and returns request metadata to be used for polling
      const response = await downloadManageCSVApi();
      setGeneratedFilesToLS(response.entity);
      apiPoll(getStatusForRequestIds, validateStatusResponse, CSV_DOWNLOAD_API_POLL_INTERVAL);
    } catch (e) {
      window.localStorage.removeItem(localStorageKey);
    }
  };

  const onSuccess = (blobData) => {
    setIsFetching(false);
    showDownloadSuccessToast();
    downloadToSystem(blobData);
  };

  const onError = (error) => {
    setIsFetching(false);
    console.error('Error in CSV download : ', error);
  };

  const onDownloadToSystemComplete = () => {
    try {
      window.localStorage.removeItem(localStorageKey);
      setIsFetching(false);
    } catch (e) {
      console.error('Error in LS : ', e);
    }
  };

  const downloadCSVBlob = async (requestId, name) => {
    try {
      const response = await downloadCSVApi(requestId);
      const fetchedBlob = await response.blob();
      onSuccess({
        fetchedBlob,
        requestId,
        name,
      });
    } catch (err) {
      console.error('Error in downloading CSV ', err);
    }
  };

  const apiPoll = async (getStatusForRequestIds, validateStatusResponse, pollingInterval) => {
    try {
      let result = await getStatusForRequestIds();
      while (validateStatusResponse(result)) {
        await wait(pollingInterval);
        result = await getStatusForRequestIds();
      }

      if (Array.isArray(result)) {
        result.forEach((result) => {
          downloadCSVBlob(result.uuid, result.name);
        });
      }
    } catch (err) {
      console.error('Error in fetching status of CSV ', err);
    }
  };

  const validateStatusResponse = (res = []) =>
    res
      .map((report) => {
        switch (report.status) {
          case 'IN_PROGRESS':
            return true;
          case 'FINISHED':
            return false;
          case 'ERROR': {
            onError('Error in generating file');
            return false;
          }
          default:
            onError('Error in generating file');

            return false;
        }
      })
      .reduce((prev, curr) => prev + curr, false);

  const getStatusForRequestIds = async () => {
    try {
      const downloadRequests = JSON.parse(window.localStorage.getItem(localStorageKey)) ?? [];

      if (downloadRequests.length) {
        const currentTS = Date.now();

        // files which were requested CSV_DOWNLOAD_FILE_REQUEST_WAIT_INTERVAL back are deleted from LS
        const validDownloadRequests = downloadRequests.filter(
          (request) => currentTS - request.requestedTS <= CSV_DOWNLOAD_FILE_REQUEST_WAIT_INTERVAL
        );

        window.localStorage.setItem(localStorageKey, JSON.stringify(validDownloadRequests ?? []));

        validDownloadRequests.forEach(() => {
          setIsFetching(true);
        });

        const response = await getStatusOfRequestIds(validDownloadRequests);
        return response;
      }
    } catch (e) {
      console.error('Error in LS : ', e);
    }
  };

  useEffect(() => {
    setIsFetching(false);
    window.localStorage.removeItem(localStorageKey);
  }, [localStorageKey]);

  return (
    <>
      <StyledMenuItem onClick={() => currentTableCsvExport()}>
        <Flex flexDir="column" p={1}>
          <TextBodyMedium bold mb="2px" sx={{ color: `${theme.colors.text.g1} !important` }}>
            Current View Download
          </TextBodyMedium>
          <TextLabel sx={{ color: `${theme.colors.text.g2} !important` }} width="250px">
            Download of review status and metrics across employees
          </TextLabel>
        </Flex>
      </StyledMenuItem>
      <StyledMenuItem onClick={() => generateFile()}>
        <Flex flexDir="column" p={1}>
          <TextBodyMedium bold mb="2px" sx={{ color: `${theme.colors.text.g1} !important` }}>
            Detailed Download
          </TextBodyMedium>
          <TextLabel sx={{ color: `${theme.colors.text.g2} !important` }} width="250px">
            Download of metrics and status for all review forms across employees
          </TextLabel>
        </Flex>
      </StyledMenuItem>
    </>
  );
}

function ReviewManageTableV1({
  gridRef,
  cohortDetails,
  performanceGridData,

  performanceGridDataLoading,
  hideRefreshAndDownload = false,
  gridStatePersistenceKey,
  showAICalibration,
}) {
  const [rowsPerPage] = useState(10);
  const [currentPage] = useState(0);
  const [orderBy] = useState({});
  const [groupFilter] = useState({});
  const [isRefreshing, setIsRefreshing] = useState(false);
  const currentCompRef = useRef();
  const agGridRef = gridRef ?? currentCompRef;
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const toast = useToast();

  const selectedCohort = useSelectedView();

  const theme = useTheme();

  const { selectedReviewCycle } = useReviewManageSidepanelData();
  const { label } = useSelectReviewsSelectedIntent() ?? {};

  const reviewCycleCreationRoute = checkNewNavEnabled()
    ? staticRoutesWithCustomSidebar.reviewCycleCreation
    : routes.settings.review.reviewCycle;

  function rowAction(rowData) {
    const empId = rowData?.[REVIEW_MANAGE_GRID_COLUMN_ID.EMPLOYEE]?.id;

    dispatch(setEmpId(empId));
    dispatch(setIsCalibrationSidepanelOpen(true));

    sendReviewCalibrationOpenEvent({
      eventProperties: {
        reviewCycleId: selectedReviewCycle?.id,
        empId,
        source: cohortDetails.name,
        intent: label,
      },
    });
  }

  const fetchTableFilters = ({ cohortDetails }) => {
    getReviewsManageFilterData({
      cohort: cohortDetails,
      filterApi: true,
    })
      .then((tableFilters) => {
        const columnData = { ...tableFilters.columnData };
        const employeeColumnDataEntries = Object.entries(tableFilters.employeeColumnData);
        employeeColumnDataEntries.forEach(([key, value]) => {
          columnData[key] = value;
        });
        // // setColumnsFilterData(columnData);
        // setFilterKeysData(tableFilters.filterKeysData);
      })
      .catch((err) => {
        console.error('tabularDataFiltersErr', err);
      })
      .finally(() => {});
  };

  useEffect(() => {
    fetchTableFilters({ cohortDetails });
  }, [cohortDetails]);

  useEffect(() => () => dispatch(setIsCalibrationSidepanelOpen(false)), [dispatch]);

  const downloadCSVApiData = () => {
    const selectedSource = { ...selectedReviewCycle };
    delete selectedSource.feedbackForms;
    return {
      cohort: cohortDetails,
      groupByKeys: {
        uuid: null,
        keyName: 'employeeId',
        displayName: 'Individual',
        enabled: true,
      },
      source: selectedSource,
      filter: null,
      orderBy: Object.values(orderBy).flat(),
      groupFilter: Object.values(groupFilter).flat(),
      attributeFilter: null,
      filterApi: false,
      offset: currentPage * rowsPerPage,
      limit: 10000,
    };
  };

  const currentTableCsvExport = useCallback(() => {
    agGridRef.current.api.exportDataAsCsv({
      fileName: 'review_explore_table.csv',
      allColumns: true,
      processCellCallback(params) {
        const {
          value,
          column: { colId },
        } = params;
        if (colId === '2') {
          return value.value.performanceGridName;
        }
        if (['40', '43', '44'].includes(colId)) {
          return reviewStatusValueGetterCSV(value);
        }
        if (['41', '42'].includes(colId)) {
          return employeesValueGetterCSV(value);
        }
        return value;
      },
    });
  }, []);

  const refreshAgGridData = useCallback(async () => {
    if (isRefreshing) {
      return;
    }
    try {
      setIsRefreshing(true);
      await queryClient.invalidateQueries([REACT_QUERY_KEYS.reviewsManage]);
    } catch (error) {
      toast({
        title: 'Error reloading. Please try again.',
        status: TOAST_STATUSES.ERROR,
        position: 'bottom-left',
      });
      console.error('Error reloading reviews table data');
    } finally {
      setIsRefreshing(false);
    }
  }, [isRefreshing, queryClient, toast]);

  if (performanceGridDataLoading) {
    return <SkeletonLoader />;
  }

  if (!selectedReviewCycle)
    return (
      <EmptyScreen
        heading={
          selectedCohort?.type === 'MY_TEAM'
            ? 'During active review cycles, you can view the list of your team members here.'
            : 'Uh-oh! It seems like there are no review cycles currently created. Start creating here'
        }
        illustrationSrc={addReviewV1}
        CTAComp={() =>
          selectedCohort?.type === 'MY_TEAM' ? null : (
            <Button
              StartIcon={Plus}
              isLink
              link={reviewCycleCreationRoute.replace(':formId', 'new').replace(':step', REVIEW_CYCLE_STEPS_V1.NAME)}
            >
              Create New Review Cycle
            </Button>
          )
        }
      />
    );

  return (
    <>
      <Flex justifyContent="space-between" alignItems="center" mb={2}>
        <AgGridSearchBar gridRef={agGridRef} display="block" width={isMobileView() ? '100%' : '320px'} />
        {!hideRefreshAndDownload && (
          <Flex>
            <AgGridRefreshButton
              tooltipLabel="Refresh Table Data"
              isRefreshing={isRefreshing}
              handleRefresh={refreshAgGridData}
            />
            <Menu>
              {({ isOpen }) => (
                <>
                  <MenuButton>
                    <Flex
                      alignItems="center"
                      justifyContent="center"
                      borderRadius="4px"
                      backgroundColor={isOpen ? '#E6F1FD' : 'transparent'}
                      width="40px"
                      height="40px"
                    >
                      <DownloadIcon style={{ cursor: 'pointer' }} />
                    </Flex>
                  </MenuButton>
                  <MenuList border="none" boxShadow="none" margin={0} padding={0} background="transparent">
                    <Flex
                      shadow={theme.shadows.large}
                      flexDir="column"
                      background="white"
                      borderRadius="8px"
                      p={2}
                      gap="4px"
                    >
                      <FileSelectionOptions
                        downloadCSVApiData={downloadCSVApiData}
                        currentTableCsvExport={currentTableCsvExport}
                      />
                    </Flex>
                  </MenuList>
                </>
              )}
            </Menu>
          </Flex>
        )}
      </Flex>
      <BulkActionContextProvider>
        <PartialPublishContextProvider>
          <BulkNotifyContextProvider>
            {!isMobileView() && (
              <BulkActionHeader
                clearSelection={() => agGridRef.current.api.deselectAll()}
                selectAll={() => agGridRef.current.api.selectAll('filtered')}
                agGridRef={agGridRef}
              />
            )}
            <ShareResultsDrawer />
            <BulkNotifyDrawer />

            <ReviewManageAGGridV1
              rowAction={rowAction}
              agGridRef={agGridRef}
              gridStatePersistenceKey={gridStatePersistenceKey}
              performanceGridData={performanceGridData}
              showAICalibration={showAICalibration}
            />
          </BulkNotifyContextProvider>
        </PartialPublishContextProvider>
      </BulkActionContextProvider>
    </>
  );
}

export { ReviewManageTableV1 };
